"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { formatCodePoints } from "@/lib/tool-kit/random-fun/emoji-data";
import {
  type FractionStyle,
  formatFraction,
  type ParseProblem,
  parseFractionLine,
  type SlashChoice,
  VULGAR_FRACTIONS,
} from "@/lib/tool-kit/random-fun/unicode-fractions";

/** English strings. A translated content file overrides these through `ui`. */
export const UNICODE_FRACTION_GENERATOR_STRINGS = {
  inputLabel: "Fractions — one per line",
  inputHint:
    "Write 3/4, a mixed number such as 2 3/16, or a negative such as -5/8.",
  inputPlaceholder: "1/2\n3/4\n17/24\n2 3/16",
  styleLabel: "Style",
  styleAuto:
    "Single character where Unicode has one (½), otherwise built (¹⁷⁄₂₄)",
  styleBuilt: "Always built from superscript and subscript digits",
  slashLabel: "Slash in built fractions",
  slashFraction: "Fraction slash ⁄ (recommended)",
  slashSolidus: "Ordinary slash /",
  reduce: "Reduce to lowest terms (6/8 becomes ¾)",
  mixed: "Write improper fractions as mixed numbers (7/4 becomes 1¾)",
  results: "Results",
  colInput: "Entered",
  colResult: "Result",
  colCodePoints: "Code points",
  colCopy: "Copy",
  copy: "Copy",
  copyLabel: "Copy {result}",
  copied: "Copied!",
  copyAll: "Copy all results",
  empty: "Type a fraction above, such as 3/4 or 17/24.",
  problemUnreadable: "Not a fraction. Use the form 3/4 or 2 3/4.",
  problemZero: "The denominator cannot be zero.",
  problemLarge: "That number is too long. Use at most nine digits.",
  commonHeading: "Single-character fractions",
  commonHint: "Click one to copy it.",
  fontNote:
    "Built fractions rely on your font. If the digits look cramped or misaligned, choose the ordinary slash.",
} as const;

type Strings = typeof UNICODE_FRACTION_GENERATOR_STRINGS;

const PROBLEM_KEY: Record<Exclude<ParseProblem, "empty">, keyof Strings> = {
  unreadable: "problemUnreadable",
  zeroDenominator: "problemZero",
  tooLarge: "problemLarge",
};

const LINE_BREAK = /\r?\n/;
const MAX_LINES = 200;

interface Row {
  input: string;
  result: string | null;
  problem: keyof Strings | null;
}

function convertLines(
  text: string,
  options: {
    style: FractionStyle;
    slash: SlashChoice;
    reduce: boolean;
    mixed: boolean;
  }
): Row[] {
  return text
    .split(LINE_BREAK)
    .slice(0, MAX_LINES)
    .map((line) => ({ line, parsed: parseFractionLine(line) }))
    .filter(
      (item) => !("problem" in item.parsed && item.parsed.problem === "empty")
    )
    .map(({ line, parsed }): Row => {
      if ("problem" in parsed) {
        return {
          input: line.trim(),
          result: null,
          problem: PROBLEM_KEY[parsed.problem as keyof typeof PROBLEM_KEY],
        };
      }
      return {
        input: line.trim(),
        result: formatFraction(parsed.fraction, options),
        problem: null,
      };
    });
}

export default function UnicodeFractionGeneratorWidget() {
  const strings = useToolUi(UNICODE_FRACTION_GENERATOR_STRINGS);
  const [text, setText] = useState("1/2\n3/4\n17/24\n2 3/16");
  const [style, setStyle] = useState<FractionStyle>("auto");
  const [slash, setSlash] = useState<SlashChoice>("fraction");
  const [reduce, setReduce] = useState(false);
  const [mixed, setMixed] = useState(false);

  const rows = convertLines(text, { style, slash, reduce, mixed });
  const good = rows.filter((row) => row.result !== null);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="ufg-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={5}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ufg-style"
          label={strings.styleLabel}
          onChange={setStyle}
          options={[
            { value: "auto", label: strings.styleAuto },
            { value: "built", label: strings.styleBuilt },
          ]}
          value={style}
        />
        <SelectField
          id="ufg-slash"
          label={strings.slashLabel}
          onChange={setSlash}
          options={[
            { value: "fraction", label: strings.slashFraction },
            { value: "solidus", label: strings.slashSolidus },
          ]}
          value={slash}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={reduce}
          label={strings.reduce}
          onChange={setReduce}
        />
        <Checkbox checked={mixed} label={strings.mixed} onChange={setMixed} />
      </div>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {rows.length === 0 ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className={TABLE}>
                <thead>
                  <tr>
                    <th className={TH} scope="col">
                      {strings.colInput}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colResult}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colCodePoints}
                    </th>
                    <th className={TH} scope="col">
                      {strings.colCopy}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    // Rows follow the input lines, which may repeat.
                    // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                    <tr key={index}>
                      <td className={`${TD} font-mono`}>{row.input}</td>
                      {row.result === null ? (
                        <td className={`${TD} text-destructive`} colSpan={3}>
                          {row.problem ? strings[row.problem] : ""}
                        </td>
                      ) : (
                        <>
                          <td className={`${TD} text-2xl`}>{row.result}</td>
                          <td className={`${TD} font-mono text-xs`}>
                            {formatCodePoints(row.result)}
                          </td>
                          <td className={TD}>
                            <CopyButton
                              copiedLabel={strings.copied}
                              label={t(strings.copyLabel, {
                                result: row.result,
                              })}
                              small
                              text={row.result}
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              disabled={good.length === 0}
              label={strings.copyAll}
              text={good.map((row) => row.result).join("\n")}
            />
            <p className={HINT}>{strings.fontNote}</p>
          </>
        )}
      </section>

      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.commonHeading}</h3>
        <p className={HINT}>{strings.commonHint}</p>
        <div className="flex flex-wrap gap-2">
          {VULGAR_FRACTIONS.map((fraction) => (
            <CopyButton
              copiedLabel={strings.copied}
              key={fraction.char}
              label={fraction.char}
              small
              text={fraction.char}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
